import { FC, useState, MouseEvent, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { Formik, useFormik } from 'formik';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import ApprovePaymentDialog from 'src/components/DialogBox/GRNPaymentDialog/ApprovePaymentDialog';
import ViewGRNPaymentDialog from 'src/components/DialogBox/GRNPaymentDialog/ViewGRNPaymentDialog';
import AddGRNPaymentDialog from 'src/components/DialogBox/GRNPaymentDialog/AddGRNPaymentDialog';
import {
  apiUrls,
  successMessages,
  confirmMessages,
  grnPaymentDialogTitles,
  grnPaymentDialogSubtitles,
  dataStorageKeywords,
  pagination,
} from 'src/utils/properties.utils';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import {
  formatNumberWithCommas,
  handleNumberFieldChange,
} from 'src/utils/format.utils';
import {
  GRNPaymentPermissions,
  getGRNPaymentPermissions,
} from 'src/utils/grnPaymentPermissions.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { GRNPaymentStatus } from 'src/models/enums/GRNStatus.enum';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import { GRNPayment } from 'src/models/Management.interface';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { grnPaymentInitialValues } from 'src/utils/initialValues.utils';
import { grnPaymentDynamicValidation } from 'src/utils/validationScheme.utils';
import { calculateRemainingAmount } from 'src/utils/grnPayment.utils';
import TextField from 'src/components/InputFields/TextField';
import PaymentModePicker from 'src/components/InputFields/PaymentModePicker';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import {
  FlexRowStartBox,
  FlexColumnBox,
  FlexColumnBoxGrn,
} from 'src/components/DialogBox/POBox/POBox.styles';
import FormikSelectField from 'src/components/InputFields/FormikSelectField';
import { useGetBank } from 'src/services/bank.service';
import { DateLabel } from '../StyledComponents/StyledPageComponents';
import dayjs from 'dayjs';
import RejectPaymentDialog from 'src/components/DialogBox/GRNPaymentDialog/RejectPaymentDialog';
import { StyledDatePickerErrorText } from '../StyledComponents/StyledUnitPicker';
import ErrorIcon from '../InputFields/ErrorIcon';

const GRNPaymentActionMenu: FC<{
  rowId: any;
  id: string;
  grnId: string;
  status: GRNPaymentStatus;
  paymentMode: PaymentMode;
  resetPageAndRefresh: () => void;
  triggerRefresh: () => void;
  deletePayment: (id: string) => void;
}> = ({
  rowId,
  id,
  grnId,
  status,
  paymentMode,
  resetPageAndRefresh,
  triggerRefresh,
  deletePayment,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewData, setViewData] = useState<GRNPayment | null>(null);
  const [loading, setLoading] = useState(false);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [permissions, setPermissions] = useState<GRNPaymentPermissions>({
    canView: false,
    canEdit: false,
    canApprove: false,
    canReject: false,
    canDelete: false,
  });
  const { bankBasics, fetchBasicDetails } = useGetBank();

  useEffect(() => {
    const fetchRole = async () => {
      const role = await getEncryptedLocalStorageData(
        dataStorageKeywords.HIGHEST_ROLE
      );
      setPermissions(getGRNPaymentPermissions(role || '', status));
    };
    fetchRole();
  }, [status]);

  const accountOptions = bankBasics.map((bank) => ({
    label: `${bank.bankName} (${bank.accountNumber})`,
    value: bank.id,
  }));

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const validationSchema = useMemo(
    () => grnPaymentDynamicValidation(remainingAmount),
    [remainingAmount]
  );

  const formik = useFormik({
    initialValues: grnPaymentInitialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      // Form submission handled by individual action handlers
    },
  });

  useEffect(() => {
    const mode = formik.values.paymentMode;
    if (mode === PaymentMode.CHEQUE || mode === PaymentMode.ONLINE) {
      fetchBasicDetails();
    }
  }, [formik.values.paymentMode]);

  const handleViewDialogOpen = async () => {
    handleClose();
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_GRN_PAYMENT_BY_ID,
        {
          params: { id: id },
        }
      );
      const paymentData = response.data.data;

      setViewData({
        id: paymentData.id,
        grnId: paymentData.ReceivedGoodId,
        paymentDate: paymentData.paymentDate,
        amount: paymentData.paidAmount,
        paymentMode: paymentData.paymentMode,
        receivedBy: paymentData.receiverName,
        status: Number(paymentData.status),
        approvedBy: paymentData.approvedBy,
        approvedDate: paymentData.approvedDate,
        notes: paymentData.rejectionNotes,
        chequeNumber: paymentData.chequeNumber,
        chequeDate: paymentData.chequeDate,
        chequeStatus: paymentData.chequeStatus,
        chequeRealizedDate: paymentData.chequeRealizedDate,
        bankName: paymentData.bankName,
        transactionReference: paymentData.paymentRefNo,
        createdAt: paymentData.createdAt,
        updatedAt: paymentData.updatedAt,
        accountNumber: paymentData.accountNumber,
      });
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(`Failed to load payment details. ${errorMessage}`, 'error');
      console.error('Error loading payment for view:', error);
    }
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setViewData(null);
  };

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      if (id) {
        await deletePayment(id);
      }
      resetPageAndRefresh();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleApproveDialogOpen = () => {
    setApproveDialogOpen(true);
    handleClose();
  };

  const handleApproveConfirm = async () => {
    try {
      const response = await axiosTokenInstance.patch(
        apiUrls.TOGGLE_GRN_PAYMENT_STATUS,
        {
          id: id,
          status: GRNPaymentStatus.APPROVED,
        }
      );
      showSnackbar(
        response.data.message || successMessages.PAYMENT_APPROVED_SUCCESSFULLY,
        'success'
      );
      triggerRefresh();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setApproveDialogOpen(false);
    }
  };

  const handleRejectDialogOpen = () => {
    setRejectDialogOpen(true);
    handleClose();
  };

  const handleRejectConfirm = async (reason: string) => {
    try {
      const response = await axiosTokenInstance.patch(
        apiUrls.TOGGLE_GRN_PAYMENT_STATUS,
        {
          id: id,
          status: GRNPaymentStatus.REJECTED,
          rejectionNotes: reason,
        }
      );
      showSnackbar(
        response.data.message || successMessages.PAYMENT_REJECTED_SUCCESSFULLY,
        'success'
      );
      triggerRefresh();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setRejectDialogOpen(false);
    }
  };

  const handleEditDialogOpen = async () => {
    handleClose();
    try {
      const [paymentResponse, grnResponse] = await Promise.all([
        axiosTokenInstance.get(apiUrls.GET_GRN_PAYMENT_BY_ID, {
          params: { id: id },
        }),
        axiosTokenInstance.get(apiUrls.GET_GRN_BY_ID, {
          params: { id: grnId },
        }),
      ]);
      const paymentData = paymentResponse.data.data;
      const grnData = grnResponse.data.data;

      const grnInvoiceAmount = Number(grnData?.invoiceAmount) || 0;
      const grnTotalPaid = Number(grnData?.totalPaidAmount) || 0;
      const currentPaymentAmount = Number(paymentData.paidAmount) || 0;
      setRemainingAmount(
        calculateRemainingAmount(
          grnInvoiceAmount,
          grnTotalPaid,
          currentPaymentAmount
        )
      );

      formik.setValues({
        id: paymentData.id,
        grnId: paymentData.grnId,
        receivedBy: paymentData.receiverName || '',
        paymentDate: paymentData.paymentDate?.split('T')[0] || '',
        amount: paymentData.paidAmount?.toString() || '',
        paymentMode: paymentData.paymentMode,
        status: paymentData.status,
        chequeNumber: paymentData.chequeNumber || '',
        chequeDate: paymentData.chequeDate?.split('T')[0] || '',
        bankAccountId: paymentData.bankAccountId || '',
        transactionReference: paymentData.paymentRefNo || '',
      });

      setEditDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(`Failed to load payment details. ${errorMessage}`, 'error');
      console.error('Error loading payment for edit:', error);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    formik.resetForm();
  };

  const handleEditSubmit = async () => {
    // Define all fields that should be touched
    const touchedFields: Record<string, boolean> = {
      receivedBy: true,
      paymentDate: true,
      amount: true,
      paymentMode: true,
    };

    // Add conditional fields based on payment mode
    if (formik.values.paymentMode === PaymentMode.CHEQUE) {
      touchedFields.chequeNumber = true;
      touchedFields.chequeDate = true;
      touchedFields.bankAccountId = true;
    } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
      touchedFields.bankAccountId = true;
      touchedFields.transactionReference = true;
    }

    // Validate using Formik's validateForm method
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(touchedFields, false);
      return;
    }

    setLoading(true);

    try {
      const payload: any = {
        id: id,
        receiverName: formik.values.receivedBy,
        paymentDate:
          typeof formik.values.paymentDate === 'string'
            ? formik.values.paymentDate.split('T')[0]
            : dayjs(formik.values.paymentDate).format('YYYY-MM-DD'),
        amount: parseFloat(formik.values.amount),
        paymentMode: formik.values.paymentMode,
      };

      if (formik.values.paymentMode === PaymentMode.CHEQUE) {
        payload.chequeNumber = formik.values.chequeNumber;
        payload.chequeDate =
          typeof formik.values.chequeDate === 'string'
            ? formik.values.chequeDate.split('T')[0]
            : dayjs(formik.values.chequeDate).format('YYYY-MM-DD');
        payload.bankAccountId = formik.values.bankAccountId;
      } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
        payload.bankAccountId = formik.values.bankAccountId;
        payload.paymentRefNo = formik.values.transactionReference;
      }

      const response = await axiosTokenInstance.put(
        apiUrls.EDIT_GRN_PAYMENT,
        payload
      );
      showSnackbar(
        response.data.message || successMessages.PAYMENT_UPDATED_SUCCESSFULLY,
        'success'
      );
      triggerRefresh();
      handleEditDialogClose();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          {permissions.canView && (
            <MenuItemWithIcon
              onClick={handleViewDialogOpen}
              iconClassName="bx bx-file-find"
              text="View"
            />
          )}
          {permissions.canEdit && (
            <MenuItemWithIcon
              onClick={handleEditDialogOpen}
              iconClassName="bx bx-edit"
              text="Edit"
            />
          )}
          {permissions.canApprove && (
            <MenuItemWithIcon
              onClick={handleApproveDialogOpen}
              iconClassName="bx bx-check"
              text="Approve"
            />
          )}
          {permissions.canReject && (
            <MenuItemWithIcon
              onClick={handleRejectDialogOpen}
              iconClassName="bx bx-x"
              text="Reject"
            />
          )}
          {permissions.canDelete && (
            <MenuItemWithIcon
              onClick={handleDeleteDialogOpen}
              iconClassName="bx bx-trash"
              text="Delete"
            />
          )}
        </MenuItemsWrapper>
      </ActionMenuPopover>
      <ViewGRNPaymentDialog
        open={viewDialogOpen}
        onClose={handleViewDialogClose}
        payment={viewData}
      />
      <ConfirmationDialog
        height="275px"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={grnPaymentDialogTitles.DELETE}
        subtitle={confirmMessages.CONFIRM_DELETE_PAYMENT}
        confirmText="Delete"
        confirmButtonColor="var(--error)"
        alertIconColor="var(--error)"
      />
      <ApprovePaymentDialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveConfirm}
        title={grnPaymentDialogTitles.APPROVE}
        subtitle={grnPaymentDialogSubtitles.APPROVE}
        subtitleSecondary={confirmMessages.CONFIRM_APPROVE_GRN_PAYMENT}
        confirmText="Approve"
      />
      <RejectPaymentDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
        title={grnPaymentDialogTitles.REJECT}
        subtitle={grnPaymentDialogSubtitles.REJECT}
        subtitleSecondary={confirmMessages.CONFIRM_REJECT_GRN_PAYMENT}
        placeholder="Reason for rejection"
        confirmText="Reject"
      />
      <AddGRNPaymentDialog
        open={editDialogOpen}
        handleClose={handleEditDialogClose}
        title={grnPaymentDialogTitles.EDIT}
        subtitle={grnPaymentDialogSubtitles.EDIT}
        onSubmit={formik.handleSubmit}
        buttonDisabled={loading}
        buttonLabel="Update"
        onSaveClick={handleEditSubmit}
        paymentModeSection={
          <>
            <PaymentModePicker
              color="var(--subtext)"
              width="251px"
              id="paymentMode"
              label="Payment Method"
              formikProps={formik}
              customMessage="Select payment method"
              showLabel={false}
            />
            {formik.values.paymentMode === PaymentMode.CHEQUE && (
              <>
                <Box
                  display={'flex'}
                  gap={'16px'}
                  justifyContent={'space-between'}
                  mb={0}
                  mt={-3}
                >
                  <FlexColumnBoxGrn>
                    <TextField
                      color="var(--subtext)"
                      width="251px"
                      id="chequeNumber"
                      label="Cheque Number"
                      formikProps={formik}
                    />
                  </FlexColumnBoxGrn>
                  <FlexColumnBoxGrn>
                    <DateLabel>Cheque Date</DateLabel>
                    <CreatedDatePicker
                      width="100%"
                      height="40px"
                      fontSize="16px"
                      color="var(--subtext)"
                      name="chequeDate"
                      placeholder="Cheque Date"
                      formikProps={formik}
                    />
                  </FlexColumnBoxGrn>
                </Box>
                <Box mb={0}>
                  <FormikSelectField
                    id="bankAccountId"
                    label="Bank Account"
                    formikProps={formik}
                    width="251px"
                    options={accountOptions}
                  />
                </Box>
              </>
            )}
            {formik.values.paymentMode === PaymentMode.ONLINE && (
              <Box
                display={'flex'}
                gap={'16px'}
                justifyContent={'space-between'}
                mb={0}
                mt={-3}
              >
                <FlexColumnBoxGrn>
                  <FormikSelectField
                    id="bankAccountId"
                    label="Bank Account"
                    formikProps={formik}
                    width="251px"
                    options={accountOptions}
                  />
                </FlexColumnBoxGrn>
                <FlexColumnBoxGrn>
                  <TextField
                    color="var(--subtext)"
                    width="251px"
                    id="transactionReference"
                    label="Payment Reference"
                    formikProps={formik}
                  />
                </FlexColumnBoxGrn>
              </Box>
            )}
          </>
        }
      >
        <Box display={'flex'} gap={'16px'} mb={0}>
          <FlexColumnBox>
            <TextField
              color="var(--subtext)"
              width="270px"
              id="receivedBy"
              label="Received By"
              formikProps={formik}
              mt={0}
              mb={2}
            />
          </FlexColumnBox>
          <FlexColumnBoxGrn>
            <CreatedDatePicker
              label="Payment Date"
              showLabel={true}
              color="var(--subtext)"
              width="100%"
              height="40px"
              name="paymentDate"
              placeholder="Payment Date"
              formikProps={formik}
              mb={2}
            />
            {formik.touched.paymentDate && formik.errors.paymentDate && (
              <StyledDatePickerErrorText>
                <ErrorIcon />
                {formik.errors.paymentDate}
              </StyledDatePickerErrorText>
            )}
          </FlexColumnBoxGrn>
        </Box>
        <Box mb={0}>
          <TextField
            color="var(--subtext)"
            width="270px"
            id="amount"
            label="Amount"
            formikProps={formik}
            type="text"
            value={formatNumberWithCommas(formik.values.amount)}
            onChange={(e) => handleNumberFieldChange(e, formik, 'amount')}
            placeholder={
              remainingAmount > 0
                ? `Remaining: ${remainingAmount.toLocaleString()}`
                : ''
            }
          />
        </Box>
      </AddGRNPaymentDialog>
    </>
  );
};

export default GRNPaymentActionMenu;
